// Signing in on the web app. Browsing and listening need no account; acting
// does (like, follow, comment, download, My Seeds). An action taken signed
// out opens this dialog, and the action runs once the person is in — they
// never have to find the button again.

import { createContext, useCallback, useContext, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@web-shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@web-shared/components/ui/dialog";
import { Input } from "@web-shared/components/ui/input";
import { getSession, requestPasswordReset, signIn, signUp, type AccountSession } from "@web-shared/lib/account";
import { identify, track } from "@web-shared/lib/analytics";
import { BetaBadge } from "./BetaBadge";

type Mode = "signin" | "signup";

/**
 * What a signed-out person was trying to do when the dialog opened — named so
 * `signup_prompted` / `signup_resumed` can say which actions bring people in
 * and which lose them.
 */
export type SignInIntent =
  | "like"
  | "follow"
  | "comment"
  | "download"
  | "clip_download"
  | "library_add"
  | "start_jam"
  | "join_jam"
  | "post_seed"
  | "event_join"
  | "save_version"
  | "save_seed"
  | "send_to_live"
  | "audiotool_upload";

/** The form both the dialog and the /signin page use. */
export function AuthForm({
  initialMode = "signin",
  initialForgot = false,
  beta,
  onDone,
}: {
  initialMode?: Mode;
  /** Open on "Reset your password" (/signin?forgot=1, the extension's link). */
  initialForgot?: boolean;
  beta: boolean;
  onDone: (session: AccountSession, how: Mode) => void;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgot, setForgot] = useState(initialForgot);
  const signingUp = mode === "signup";
  const ready = email.trim() !== "" && password !== "" && (!signingUp || username.trim() !== "");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const session = signingUp
        ? await signUp(email.trim(), password, username.trim())
        : await signIn(email.trim(), password);
      identify();
      if (signingUp) track("account_created", { detail: "web" });
      onDone(session, mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : signingUp ? "Could not create the account." : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  if (forgot) return <ForgotForm initialEmail={email} onBack={() => setForgot(false)} />;

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex items-center gap-2">
        <img src="/seedbed-logo.webp" alt="" className="size-5" />
        <h1 className="text-sm font-semibold">{signingUp ? "Join Seedbed" : "Sign in to Seedbed"}</h1>
        {beta && <BetaBadge />}
      </div>
      <p className="text-xs text-muted-foreground">
        {signingUp
          ? "One account for the web and for Seedbed inside Live."
          : beta
            ? "Use your beta account — the one the beta build of the extension signs in with."
            : "Use the account you sign in with inside Live."}
      </p>
      {signingUp && (
        <Input
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Producer name"
          aria-label="Producer name"
        />
      )}
      <Input
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        aria-label="Email"
      />
      <Input
        type="password"
        autoComplete={signingUp ? "new-password" : "current-password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={signingUp ? "Password (8+ characters)" : "Password"}
        aria-label="Password"
      />
      {!signingUp && (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setForgot(true);
          }}
          className="-mt-2 block cursor-pointer text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          Forgot password?
        </button>
      )}
      {error && <p className="text-xs text-removed">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy || !ready}>
        {busy ? (signingUp ? "Creating account…" : "Signing in…") : signingUp ? "Create account" : "Sign in"}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        {signingUp ? "Already have an account? " : "New to Seedbed? "}
        <button
          type="button"
          className="cursor-pointer text-primary hover:underline"
          onClick={() => {
            setError(null);
            setMode(signingUp ? "signin" : "signup");
          }}
        >
          {signingUp ? "Sign in" : "Create an account"}
        </button>
      </p>
    </form>
  );
}

/** Forgot password: an email, then "check your inbox". */
function ForgotForm({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex items-center gap-2">
        <img src="/seedbed-logo.webp" alt="" className="size-5" />
        <h1 className="text-sm font-semibold">Reset your password</h1>
      </div>
      {sent ? (
        <p className="text-sm text-muted-foreground">
          If <span className="font-semibold text-foreground">{email.trim()}</span> has a Seedbed account, a link to set a new password is on its way. It works for an hour.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">We'll email you a link to set a new one.</p>
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" aria-label="Email" />
          {error && <p className="text-xs text-removed">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy || !email.trim()}>
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </>
      )}
      <p className="text-center text-xs">
        <button type="button" onClick={onBack} className="cursor-pointer text-primary hover:underline">
          Back to sign in
        </button>
      </p>
    </form>
  );
}

interface AuthGate {
  /**
   * Run `action` as a signed-in person: now, if they are; after they sign in
   * or up, if not. `why` titles the dialog ("Sign in to like this"); `mode`
   * picks which form it opens on (sign in, unless the page expects newcomers).
   * `intent` names the action for analytics.
   */
  requireSignIn: (intent: SignInIntent, why: string, action: () => void, mode?: Mode) => void;
  /** Open the dialog with nothing to run after. */
  openSignIn: (mode?: Mode) => void;
}

const AuthGateContext = createContext<AuthGate | null>(null);

export function useAuthGate(): AuthGate {
  const gate = useContext(AuthGateContext);
  if (!gate) throw new Error("useAuthGate outside <AuthGateProvider>.");
  return gate;
}

export function AuthGateProvider({ beta, children }: { beta: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("signin");
  const pending = useRef<{ intent: SignInIntent; run: () => void } | null>(null);

  const requireSignIn = useCallback((intent: SignInIntent, reason: string, action: () => void, m: Mode = "signin") => {
    if (getSession()) {
      action();
      return;
    }
    track("signup_prompted", { detail: intent });
    pending.current = { intent, run: action };
    setWhy(reason);
    setMode(m);
    setOpen(true);
  }, []);

  const openSignIn = useCallback((m: Mode = "signin") => {
    pending.current = null;
    setWhy(null);
    setMode(m);
    setOpen(true);
  }, []);

  const gate = useMemo(() => ({ requireSignIn, openSignIn }), [requireSignIn, openSignIn]);

  return (
    <AuthGateContext.Provider value={gate}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) pending.current = null;
        }}
      >
        {/* Above the app's z-60 sheets (the Ableton and DAW pickers), so a
            sheet stays open underneath and picks up where it was once the
            person is in. */}
        <DialogContent className="z-[70] w-[calc(100%-2rem)] max-w-sm" overlayClassName="z-[70]">
          <DialogTitle className="sr-only">{why ?? "Sign in"}</DialogTitle>
          <DialogDescription className="sr-only">Sign in or create a Seedbed account.</DialogDescription>
          {why && <p className="text-sm font-medium">{why}</p>}
          <AuthForm
            key={`${mode}:${open}`}
            initialMode={mode}
            beta={beta}
            onDone={(_session, how) => {
              setOpen(false);
              const next = pending.current;
              pending.current = null;
              if (!next) return;
              track("signup_resumed", { detail: `${next.intent}|${how}` });
              next.run();
            }}
          />
        </DialogContent>
      </Dialog>
    </AuthGateContext.Provider>
  );
}
